/**
 * API client.
 *
 * The access token lives in memory only — never localStorage — so an XSS cannot
 * walk off with a long-lived credential. Persistence comes from the httpOnly
 * refresh cookie, which JavaScript cannot read at all. On a 401 the client
 * refreshes once and replays the request; concurrent 401s share a single
 * refresh promise so a page with six queries does not rotate six times.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
/** Why the last refresh failed: the server said no, or there was no server to ask. */
let lastRefreshUnreachable = false;
const listeners = new Set<(token: string | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener(token);
}

export const getAccessToken = (): string | null => accessToken;

export function onAccessTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the refresh-and-retry dance (used by the refresh call itself). */
  raw?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function refreshAccessToken(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      lastRefreshUnreachable = false;
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
      });
      // A gateway error is the proxy answering for an API that is not there.
      if (response.status >= 502 && response.status <= 504) lastRefreshUnreachable = true;
      if (!response.ok) {
        setAccessToken(null);
        return false;
      }
      const data = (await response.json()) as { tokens?: { accessToken?: string } };
      if (!data.tokens?.accessToken) {
        setAccessToken(null);
        return false;
      }
      setAccessToken(data.tokens.accessToken);
      return true;
    } catch {
      lastRefreshUnreachable = true;
      setAccessToken(null);
      return false;
    } finally {
      // Release the shared promise on the next tick so callers awaiting it all
      // observe the same result before a new refresh can start.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();
  return refreshPromise;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const data = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    return new ApiError(
      response.status,
      data.error?.code ?? 'error',
      data.error?.message ?? `Ошибка ${response.status}`,
      data.error?.details,
    );
  } catch {
    return new ApiError(response.status, 'error', `Ошибка ${response.status}`);
  }
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, raw, query, headers, ...rest } = options;

  const isFormData = body instanceof FormData;
  const send = async (): Promise<Response> =>
    fetch(buildUrl(path, query), {
      ...rest,
      credentials: 'include',
      headers: {
        ...(isFormData || body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(headers as Record<string, string> | undefined),
      },
      ...(body !== undefined
        ? { body: isFormData ? (body as FormData) : JSON.stringify(body) }
        : {}),
    });

  let response = await send();

  if (response.status === 401 && !raw) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await send();
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await response.text()) as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    apiFetch<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  /**
   * Saves a text export (GPX, KML) as a file. A plain `<a href download>`
   * carries no access token, so a private trip answered its own owner 404.
   */
  download: async (path: string, filename: string, type: string) => {
    const text = await apiFetch<string>(path, { method: 'GET' });
    const url = URL.createObjectURL(new Blob([text], { type }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  upload: <T>(path: string, form: FormData) => apiFetch<T>(path, { method: 'POST', body: form }),
  /**
   * Bootstrap: try to restore a session from the refresh cookie on page load.
   * `unreachable` is not `denied`: with no network the session may well be
   * fine, and the app opens from the device's copy (`lib/offline`).
   */
  restoreSession: async (): Promise<'ok' | 'denied' | 'unreachable'> => {
    if (await refreshAccessToken()) return 'ok';
    return lastRefreshUnreachable ? 'unreachable' : 'denied';
  },
};
